import { injectable } from 'tsyringe';
import { DelegateProvider } from '../interfaces/delegate-provider.interface';

// -----------------------------------------------------------------------------
export interface ITimerServiceDelegate {
  onTimer(id?: string): Promise<void> | void;
}

// -----------------------------------------------------------------------------
export interface TimerServiceOptions {
  intervalInMs: number;
  id?: string;
  autoReset?: boolean;
}

// -----------------------------------------------------------------------------
// Usage:
// If you only wish to use the |sleep| method - you do not need to call
// initialize.
// Otherwise, first make sure you call |initialize| and |addListener| then call
// |start|
@injectable()
export class TimerService implements DelegateProvider<ITimerServiceDelegate> {
  // ---------------------------------------------------------------------------
  private options: TimerServiceOptions;
  private timerId: number | null = null;
  private readonly listeners: Set<ITimerServiceDelegate>;

  // ---------------------------------------------------------------------------
  public constructor() {
    this.listeners = new Set<ITimerServiceDelegate>();
  }

  // ---------------------------------------------------------------------------
  public initialize(options: TimerServiceOptions) {
    this.options = options;
  }

  // ---------------------------------------------------------------------------
  public async sleep(intervalInMS: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, intervalInMS);
    });
  }

  // ---------------------------------------------------------------------------
  public start(): void {
    this.stop();

    const { intervalInMs: intervalInMS } = this.options;
    this.timerId = setTimeout(this.handleTimerEvent, intervalInMS) as any;
  }

  // ---------------------------------------------------------------------------
  public stop(): void {
    if (this.timerId == null) {
      return;
    }

    clearTimeout(this.timerId);
    this.timerId = null;
  }

  // ---------------------------------------------------------------------------
  private handleTimerEvent = async () => {
    this.timerId = null;

    for (const listener of this.listeners) {
      try {
        await listener.onTimer(this.options.id);
      } catch {}
    }

    if (this.options.autoReset) {
      this.start();
    }
  };

  // ---------------------------------------------------------------------------
  // DelegateProvider
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  addListener(listener: ITimerServiceDelegate) {
    this.listeners.add(listener);
  }

  // ---------------------------------------------------------------------------
  removeListener(listener: ITimerServiceDelegate) {
    this.listeners.delete(listener);
  }
}
